import { useContext, useCallback } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import { modalContext } from '@contexts'
import { useIntegrationAssignment } from '@global/hooks'
import { templateCopyContext } from '../../../../context'

const FullWidth = styled.div`
  width: 100%;
`
const NoMarginTypography = styled(Typography)`
  margin-top: 0px;
`
const TypographyBold = styled(Typography)`
  weight: 500;
`

export interface DataOption {
  type: string
  label: string
  icon?: any
}

interface DataSelectionProps {
  integrationAssignment: any
  metadataKey: string
  selectedDataType: string | undefined
  options: DataOption[]
  resetIntegrationOnChange?: boolean
  filtersToReset?: string[]
  disabled?: boolean
}

export const DataSelection = ({
  integrationAssignment,
  metadataKey,
  selectedDataType,
  options,
  resetIntegrationOnChange,
  filtersToReset = [],
  disabled,
}: DataSelectionProps) => {
  const ModalContext = useContext(modalContext)
  const { onChange } = useContext<any>(templateCopyContext)

  const { onIntegrationMetadataChange, onIntegrationResetByDataTypeChange } =
    useIntegrationAssignment({ integrationAssignment, onChange })
  const dataSelectionChangeModal = useCallback(
    (dataValue: string) => {
      ModalContext.displayModal({
        title: t`Are you sure?`,
        description: t`Changing your selected data type will reset any filters and field mappings currently configured for this integration.`,
        confirm: () => () => {
          onIntegrationResetByDataTypeChange(dataValue, metadataKey, filtersToReset)
        },
      })
    },
    [ModalContext, metadataKey, filtersToReset, onIntegrationResetByDataTypeChange],
  )

  return (
    <FullWidth>
      <TypographyBold variant="h4">{t`Data selection`}</TypographyBold>
      <NoMarginTypography variant="body2">
        {t`Select from the options below what type of data you'd like to sync`}
      </NoMarginTypography>
      <RadioGroup
        aria-label="Data Type"
        name="dataType"
        value={selectedDataType}
        onChange={event => {
          if (resetIntegrationOnChange) {
            dataSelectionChangeModal(event.target.value)
          } else {
            onIntegrationMetadataChange(event.target.value, metadataKey)
          }
        }}
      >
        {options.map((option, index) => (
          <FormControlLabel
            value={option.type}
            control={<Radio />}
            label={<LabelElement Icon={option.icon} description={option.label} />}
            disabled={disabled || false}
            key={`option-${index}`}
          />
        ))}
      </RadioGroup>
    </FullWidth>
  )
}

function LabelElement({ Icon, description }) {
  return (
    <CenteredIconTypography>
      {Icon && <Icon size="small" />}
      <span>{description}</span>
    </CenteredIconTypography>
  )
}

const CenteredIconTypography = styled(Typography)`
  display: flex;
  align-items: center;

  span {
    padding-left: 8px;
  }
`
